import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import type { Theme } from '@mui/material/styles'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box'
  },
  icon: {
    marginRight: theme.spacing(2),
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 24,
    flexShrink: 0
  },
  message: {
    fontSize: '1rem',
    lineHeight: 1.5
  },
  title: {
    fontWeight: 1000,
    marginBottom: theme.spacing(0.5)
  },
  // Estilos para warning
  warningRoot: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ffc107'
  },
  warningIcon: {
    color: '#ff9800'
  },
  warningText: {
    color: '#db9730'
  },
  // Estilos para error
  errorRoot: {
    backgroundColor: '#ffebee',
    border: '1px solid #f44336'
  },
  errorIcon: {
    color: '#f44336'
  },
  errorText: {
    color: '#c62828'
  },
  // Estilos para success
  successRoot: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50'
  },
  successIcon: {
    color: '#4caf50'
  },
  successText: {
    color: '#5C8E2F'
  },
  // Estilos para info
  infoRoot: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3'
  },
  infoIcon: {
    color: '#2196f3'
  },
  infoText: {
    color: '#1565c0'
  }
}))

type Severity = 'warning' | 'error' | 'success' | 'info'

interface CustomAlertProps {
  message?: string | React.ReactNode
  title?: string
  severity?: Severity
  icon?: React.ReactElement
  maxWidth?: number | string
  maxHeight?: number | string
  /**
   * Variante compacta: saca el margen superior fijo y achica el padding para
   * que el aviso ocupe lo minimo. Pensada para cuando el alert va alineado con
   * otro contenido en la misma fila, en vez de apilado debajo (que es el caso
   * para el que se penso el `mt` por defecto).
   */
  dense?: boolean
  messageColor?: string
  classes?: {
    root?: string
    icon?: string
    message?: string
    title?: string
  }
}

const CustomAlert: React.FC<CustomAlertProps> = ({
  message = 'Error al intentar obtener la información.',
  title,
  severity = 'warning',
  icon,
  maxWidth,
  maxHeight,
  dense = false,
  messageColor,
  classes: customClasses
}) => {
  const defaultClasses = useStyles()

  // Mapeo de íconos por defecto según severity
  const defaultIcons: Record<Severity, React.ReactElement> = {
    warning: <WarningIcon />,
    error: <ErrorIcon />,
    success: <CheckCircleIcon />,
    info: <InfoIcon />
  }

  // Clases según severity
  const rootClass =
    customClasses?.root ||
    `${defaultClasses.root} ${defaultClasses[`${severity}Root`]}`

  const iconClass = `${defaultClasses.icon} ${
    defaultClasses[`${severity}Icon`]
  } ${customClasses?.icon || ''}`.trim()

  const IconComponent = React.cloneElement(icon || defaultIcons[severity], {
    className: iconClass
  })

  const messageClass = `${defaultClasses.message} ${
    defaultClasses[`${severity}Text`]
  } ${customClasses?.message || ''}`.trim()

  const titleClass =
    customClasses?.title ||
    `${defaultClasses.title} ${defaultClasses[`${severity}Text`]}`

  return (
    <Box
      mt={dense ? 0 : 1}
      className={rootClass}
      style={{
        maxWidth,
        maxHeight,
        overflow: maxHeight ? 'hidden' : undefined,
        padding: dense ? '3px 8px' : maxHeight ? 8 : undefined
      }}
    >
      <Box component='span' className={iconClass}>
        {IconComponent}
      </Box>
      <Box>
        {title && (
          <Box className={titleClass}>
            <Typography>{title}</Typography>
          </Box>
        )}
        <Typography className={messageClass} style={{ color: messageColor }}>
          {message}
        </Typography>
      </Box>
    </Box>
  )
}

export default CustomAlert
